import { DatePicker, Input, Radio, Select, Form } from "antd";
import { FormInstance } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import * as React from "react";
import moment from "moment";
import { TableAPI } from "../../API/TableAPI";
import FormLayouts from "../../FormLayouts";
import { makeEntryModal } from "./CreateEntryModals";
import "./CreateEntryModals.css";
import Entry from "../../Model/Entry";

const Option = Select.Option;

function renderForm(form: FormInstance) {
  return (
    <Form
      className="modal-form"
      hideRequiredMark={true}
      form={form}
      initialValues={{
        isWavelengthApplicable: true,
        isSatelliteActive: true,
      }}
    >
      <span>For multiple parameters please create multiple entries</span>,
      <Form.Item
        name="parameter"
        label="Parameter"
        rules={[
          { required: true, message: "Parameter is required" },
          {
            validator: (
              _,
              value: string,
              callback: (message?: string) => void
            ) => {
              // Do nothing on empty strings
              if (value.length === 0) {
                callback();
                return;
              }

              const index = value.split(" ").findIndex((w) => {
                if (w.length === 0) {
                  return false;
                } else {
                  return /[a-z]/.test(w[0]);
                }
              });
              if (index >= 0) {
                callback("Please use capitalised letters");
              } else {
                callback();
              }
            },
          },
        ]}
      >
        <Input placeholder="ex. Aerosol Optical Depth, Backscatter Coefficient, ..." />
      </Form.Item>
      <Form.Item name="satelliteName" label="Satellite Name" required>
        <Input />
      </Form.Item>
      <Form.Item
        name="instrument"
        label="Instrument name"
        help="For multiple instruments of the same satellite create new entries"
        required
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="isWavelengthApplicable"
        label="Is wavelength applicable to this parameter?"
        {...FormLayouts.veryLong}
      >
        <Radio.Group buttonStyle="solid">
          <Radio.Button value={true}>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.isWavelengthApplicable !== current.isWavelengthApplicable
        }
      >
        {({ getFieldValue }) =>
          getFieldValue("isWavelengthApplicable") ? (
            <Form.Item
              name="spectralRange"
              label="Wavelength (nm)"
              help="For multiple values, you can input ranges (532-600) or comma-separated values (532, 628, 1024)"
              rules={[]}
            >
              <Input placeholder="ex. 532 nm" />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      <Form.Item
        name="unit"
        label="Unit"
        rules={[
          { required: true, message: "If not applicable, use 'Unitless'" },
        ]}
      >
        <Input placeholder="ex. km-1 sr-1" />
      </Form.Item>
      <Form.Item name="passive" label="Active/Passive" required>
        <Select>
          <Option value="true">Passive</Option>
          <Option value="false">Active</Option>
        </Select>
      </Form.Item>
      <Form.Item
        name="temporalResolution"
        label="Temporal Resolution"
        rules={[{ required: true, message: "This field is not optional" }]}
      >
        <Input placeholder="ex. Daily, Sub-daily, 5-min, ..." />
      </Form.Item>
      <Form.Item
        name="spatialResolution"
        label="Spatial Resolution"
        rules={[
          { required: true, message: "This field is not optional" },
          {
            pattern: /\d+(\.\d+)* \w+ x \d+(\.\d+)* \w+/g,
            message: 'Spatial resolution must be in "X unit x Y unit" format',
          },
        ]}
      >
        <Input placeholder="ex. 1 deg x 1 deg, 10 km x 70 m, ..." />
      </Form.Item>
      <Form.Item name="verticalResolution" label="Vertical Resolution" required>
        <Input placeholder="ex. Columnar, 30-180m, ..." />
      </Form.Item>
      <Form.Item
        name="coverage"
        label="Coverage"
        rules={[
          { required: true, message: "This field is not optional" },
          { pattern: /[A-Z].*/g, message: "Please use capitalised words" },
        ]}
      >
        <Input placeholder="ex. Global, Oceans, ..." />
      </Form.Item>
      <Form.Item
        name="isSatelliteActive"
        label="Is the satellite still active?"
        {...FormLayouts.long}
      >
        <Radio.Group buttonStyle="solid">
          <Radio.Button value={true}>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="dataAvailabilityFrom"
        label="Data available from"
        rules={[{ required: true, message: "This field is required" }]}
      >
        <DatePicker format="YYYY-MM-DD" />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.isSatelliteActive !== current.isSatelliteActive
        }
      >
        {({ getFieldValue }) =>
          getFieldValue("isSatelliteActive") === false ? (
            <Form.Item name="dataAvailabilityTo" label="Data available to">
              <DatePicker format="YYYY-MM-DD" />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      <Form.Item
        name="openData"
        label="Open data?"
        rules={[{ required: true, message: "Please make a selection" }]}
      >
        <Radio.Group buttonStyle="solid">
          <Radio.Button value={true}>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="productView"
        label="Product View"
        rules={[
          { type: "url", message: "Must be a valid URL" },
          { required: true, message: "This field is not optional" },
        ]}
      >
        <Input type="url" placeholder="Include 'http://' or 'https://'" />
      </Form.Item>
      <Form.Item
        name="dataRepository"
        label="Data Repository"
        rules={[
          { type: "url", message: "Must be a valid URL" },
          { required: true, message: "This field is not optional" },
        ]}
      >
        <Input
          type="url"
          placeholder="Include 'http://', 'https://', 'ftp://' or 'sftp://'"
        />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, current) => prev.openData !== current.openData}
      >
        {({ getFieldValue }) => {
          // If this dataset is not open-access, require a contact address
          const isOpenData = getFieldValue("openData") as boolean;
          const rules = [];

          if (!isOpenData) {
            rules.push({
              required: true,
              message: "This field is required for closed-access datasets",
            });
          } else {
            rules.push({ required: false });
          }

          return (
            <Form.Item name="contact" label="Contact Address" rules={rules}>
              <Input type="email" placeholder="address@example.com" />
            </Form.Item>
          );
        }}
      </Form.Item>
      <Form.Item
        name="references"
        label="References"
        rules={[{ required: true, message: "Please insert some references" }]}
      >
        <TextArea />
      </Form.Item>
    </Form>
  );
}

function formToEntry(form: FormInstance) {
  // Format dates
  const dataAvailabilityFromRaw = form.getFieldValue("dataAvailabilityFrom");
  const dataAvailabilityFrom =
    dataAvailabilityFromRaw === undefined
      ? ""
      : dataAvailabilityFromRaw.format("YYYY-MM-DD");

  let dataAvailabilityTo = null;
  if (!form.getFieldValue("isSatelliteActive")) {
    const dataAvailabilityToRaw = form.getFieldValue("dataAvailabilityTo");
    dataAvailabilityTo =
      dataAvailabilityToRaw === undefined
        ? ""
        : dataAvailabilityToRaw.format("YYYY-MM-DD");
  }

  // Create body
  return {
    parameter: form.getFieldValue("parameter"),
    satelliteName: form.getFieldValue("satelliteName"),
    instrument: form.getFieldValue("instrument"),
    spectralRange: (form.getFieldValue("isWavelengthApplicable") as boolean)
      ? form.getFieldValue("spectralRange")
      : "N/A",
    unit: form.getFieldValue("unit"),
    passive: form.getFieldValue("passive") === "true", // convert to bool
    temporalResolution: form.getFieldValue("temporalResolution"),
    spatialResolution: form.getFieldValue("spatialResolution"),
    verticalResolution: form.getFieldValue("verticalResolution"),
    coverage: form.getFieldValue("coverage"),
    dataAvailabilityFrom,
    dataAvailabilityTo,
    openData: form.getFieldValue("openData"),
    productView: form.getFieldValue("productView"),
    dataRepository: form.getFieldValue("dataRepository"),
    contact: form.getFieldValue("contact"),
    references: form.getFieldValue("references"),
  };
}

function entryToForm(form: FormInstance, entry: Entry) {
  console.log(entry);

  // Spectral range applicability checkbox
  let spectralRange = entry.spectralRange;
  if (spectralRange === "N/A") {
    spectralRange = "";
    form.setFieldsValue({
      isWavelengthApplicable: false,
    });
  } else {
    form.setFieldsValue({
      isWavelengthApplicable: true,
    });
  }

  // Parse date
  const momentFrom = moment(entry.dataAvailabilityFrom, "YYYY-MM-DD");

  let momentTo: moment.Moment | null;
  if (entry.dataAvailabilityTo === null) {
    momentTo = null;
    form.setFieldsValue({ isSatelliteActive: true });
  } else {
    momentTo = moment(entry.dataAvailabilityTo, "YYYY-MM-DD");
    form.setFieldsValue({ isSatelliteActive: false });
  }

  form.setFieldsValue({
    ...entry,
    passive: entry.passive ? "true" : "false",
    dataAvailabilityFrom: momentFrom,
    dataAvailabilityTo: momentTo,
  });
}

export const CreateSatelliteModal = makeEntryModal(
  "satellite",
  new TableAPI("satellites"),
  renderForm,
  formToEntry,
  entryToForm
);
